import { apyToAprPercent } from '../../../utils/parsing'
import { YieldFetcher } from '../../types'

const HBHYPE_URL =
  'https://api.hyperbeat.org/api/v1/staking?address=0xCeaD893b162D38e714D82d06a7fe0b0dc3c38E0b'
const HBUHYPE_URL =
  'https://app.hyperbeat.org/api/hyperfolio/vault-infos?vaultAddress=0x96C6cBB6251Ee1c257b2162ca0f39AA5Fa44B1FB'

const hbhype = 'hyperbeat x ether.fi HYPE::beHYPE'
const hbuhype = 'Hyperbeat Ultra Hype::hbHYPE'

export const hbhypeFetcher: YieldFetcher = {
  label: 'HBHYPE',
  fetch: async () => {
    try {
      const response = await fetch(HBHYPE_URL)
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`)
      }

      const result = await response.json()

      if (
        !result.success ||
        !result.data ||
        !Array.isArray(result.data.delegations)
      ) {
        throw new Error('Invalid response structure')
      }

      const delegations = result.data.delegations

      const totalApr = delegations.reduce(
        (sum: any, validator: any) => sum + Number(validator.apr ?? 0),
        0,
      )
      const averageApr = totalApr / delegations.length

      return { [hbhype]: averageApr }
    } catch (e) {
      return { [hbhype]: 0 }
    }
  },
}

export const hbuhypeFetcher: YieldFetcher = {
  label: 'HBUHYPE',
  fetch: async () => {
    try {
      const response = await fetch(HBUHYPE_URL)
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`)
      }
      const result = await response.json()
      const apy = result.currentAPY?.apy_1d ?? 0

      return { [hbuhype]: apyToAprPercent(apy) }
    } catch (e) {
      return { [hbuhype]: 0 }
    }
  },
}

/**
 * The four live core Hyperbeat vaults (Vault-Infra generation), keyed by
 * the token-list assetGroup each savings row uses as its `yieldKey`.
 *
 * NOT in this list on purpose: hbXAUt, wVLP, dnHYPE and dnPUMP — all four
 * are flagged `windingDown` in Hyperbeat's own app config (2026-08-18);
 * nLP is Native's wNLP and belongs to the `native` provider; hbHYPE and
 * hbBTC are Upshift-era vaults already served elsewhere.
 *
 * A rate here is denominated in the row's UNDERLYING: HYPE for lstHYPE,
 * beHYPE for liquidHYPE (the underlying's own staking yield is a separate
 * leg carried by beHYPE's assetGroup), dollars for the two stables.
 */
const HYPERBEAT_VAULTS: ReadonlyArray<{ address: string; key: string }> = [
  {
    address: '0x5e105266db42f78FA814322Bce7f388B4C2e61eb',
    key: 'Hyperbeat USDT::hbUSDT',
  },
  {
    address: '0x057ced81348D57Aad579A672d521d7b4396E8a61',
    key: 'Hyperbeat USDC::hbUSDC',
  },
  {
    address: '0x81e064d0eB539de7c3170EDF38C1A42CBd752A76',
    key: 'Hyperbeat LST Vault::lstHYPE',
  },
  {
    address: '0x441794D6a8F9A3739F5D4E98a728937b33489D29',
    key: 'Liquid HYPE Yield::liquidHYPE',
  },
]

const HYPERBEAT_VAULT_APY_URL = (address: string) =>
  `https://api.hyperbeat.org/api/v1/vaults/apy/${address}?limit=1`

/**
 * One fetcher for every core-vault row — `resolveEntryApr` dedups by
 * object identity, so the four registry rows referencing this run the
 * batch once per fetch cycle.
 *
 * Source: Hyperbeat's own trailing-APY series (the number their app
 * headlines is `apy_7d`). Values arrive as PERCENT APY; converted to the
 * package's nominal-APR convention. A vault whose endpoint fails reports
 * 0 rather than poisoning the batch.
 */
export const hyperbeatVaultsFetcher: YieldFetcher = {
  label: 'HYPERBEAT_VAULTS',
  fetch: async () => {
    const out: { [key: string]: number } = {}
    await Promise.all(
      HYPERBEAT_VAULTS.map(async ({ address, key }) => {
        try {
          const response = await fetch(HYPERBEAT_VAULT_APY_URL(address))
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          const result = await response.json()
          const apy = Number(
            result.current_apy?.apy_7d ?? result.current_apy?.apy ?? 0,
          )
          out[key] = apyToAprPercent(apy)
        } catch (e) {
          out[key] = 0
        }
      }),
    )
    return out
  },
}
